import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEditorialTitle } from '../titleContext'
import { canPublish, useEditorialMe } from '../me'
import { etDate } from '../labels'
import { ApiError } from '../../api/client'
import { describeError } from '../../lib/describeError'
import { CheckChips } from './RidesDeskPage'
import { AwardPanel } from './AwardPanel'
import { afterSave, diffPatch, isDirty, reconcile, toDraft, type Draft, type DraftState } from './rideDraft'
import {
  CLASSES, DECLINE_REASONS, PART_TYPES, STATUS_LABEL, approve, askPhotos, decline, merge, moveItem, patchRide, publishNewLabel, restore,
  awardLabel, magazinePackageHref, retire, ridesHref, ride as loadRide, shortlist, unpublishedFinished, type DeclineReason,
} from './ridesApi'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const field = `min-h-11 w-full rounded-lg border border-ink-400 bg-ink-800 px-3 font-ui text-[14px] text-paper placeholder:text-paper-faint ${focus}`
const btn = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-4 font-ui text-[14px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-50 ${focus}`
const btnSm = `inline-flex min-h-8 items-center justify-center rounded-md border border-ink-400 px-2.5 font-ui text-[12px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-40 ${focus}`
const btnPrimary = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-4 font-ui text-[14px] font-semibold text-ink-900 hover:opacity-90 disabled:opacity-50 ${focus}`
const btnDanger = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-radar-red px-4 font-ui text-[14px] font-semibold text-radar-red hover:bg-radar-red/10 disabled:opacity-50 ${focus}`
const lab = 'flex flex-col gap-1 font-ui text-[13px] font-semibold text-paper-dim'

type Confirm = null | 'decline' | 'merge' | 'retire' | 'award'

export function RideReviewPage() {
  // Keyed on the ride so a different ride starts with clean state.
  return <RideReview key={useParams().id} />
}

function RideReview() {
  const { section, publicationSlug: pub } = useEditorialTitle()
  const id = Number(useParams().id)
  const me = useEditorialMe()
  const editor = canPublish(me.data)
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['rides-ride', pub, id], queryFn: () => loadRide(pub, id), enabled: section.readers_rides === true && Number.isFinite(id) })
  const [st, setSt] = useState<DraftState>({ draft: null, base: null })
  const { draft } = st
  const [confirm, setConfirm] = useState<Confirm>(null)
  const [reason, setReason] = useState<DeclineReason>('not_rc')
  const [note, setNote] = useState('')
  const [intoId, setIntoId] = useState('')
  const [retireWhy, setRetireWhy] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (q.data) { const d = toDraft(q.data); setSt((s) => reconcile(s, d)) }
  }, [q.data])

  const r = q.data
  const dirty = isDirty(st)
  useEffect(() => { if (dirty) setConfirm(null) }, [dirty])
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['rides-queue', pub] }); return qc.invalidateQueries({ queryKey: ['rides-ride', pub, id] }) }
  const run = (fn: () => Promise<unknown>, ok: string) => ({
    mutationFn: fn,
    onSuccess: () => { setError(null); setMessage(ok); setConfirm(null); void refresh() },
    onError: (e: unknown) => {
      setMessage(null); setError(describeError(e))
      if (e instanceof ApiError && e.status === 409) void refresh()
    },
  })

  const save = useMutation({
    mutationFn: (sent: Draft) => patchRide(pub, id, diffPatch(sent, st.base as Draft)),
    onSuccess: (saved, sent) => {
      setError(null); setMessage('Saved.')
      setSt((s) => (s.draft ? afterSave(s.draft, sent, toDraft(saved)) : s))
      void refresh()
    },
    onError: (e: unknown) => {
      setMessage(null); setError(describeError(e))
      if (e instanceof ApiError && e.status === 409) void refresh()
    },
  })
  const doApprove = useMutation(run(() => approve(pub, id), 'Approved. The ride is live and the builder has been told.'))
  const doPublishNew = useMutation(run(() => approve(pub, id), 'Published. The new photos are on the ride page.'))
  const doAsk = useMutation(run(() => askPhotos(pub, id), 'Sent. The builder has a link to add better photos.'))
  const doDecline = useMutation(run(() => decline(pub, id, reason, note), 'Declined. The builder has been told.'))
  const doMerge = useMutation(run(() => merge(pub, id, Number(intoId)), 'Merged.'))
  const doStar = useMutation(run(() => shortlist(pub, id, !r?.shortlisted), r?.shortlisted ? 'Taken off the shortlist.' : 'Added to the shortlist.'))
  const doRetire = useMutation(run(() => retire(pub, id, retireWhy), 'Retired. Its page now shows the reason.'))
  const doRestore = useMutation(run(() => restore(pub, id), 'Restored. The ride is live again.'))
  const busy = [save, doApprove, doPublishNew, doAsk, doDecline, doMerge, doStar, doRetire, doRestore].some((m) => m.isPending)

  const set = (patch: Partial<Draft>) => setSt((s) => (s.draft ? { ...s, draft: { ...s.draft, ...patch } } : s))
  const photoById = new Map((r?.photos ?? []).map((p) => [p.id, p]))
  if (section.readers_rides !== true) return <Navigate to={`/editorial/${section.path}`} replace />
  const reviewable = r && (r.status === 'pending' || r.status === 'needs_photos')
  const newPhotos = r ? unpublishedFinished(r) : 0

  return (
    <div className="flex flex-col gap-5 font-ui text-paper">
      <Link to={ridesHref(section.path)} className={`w-fit text-[13px] font-semibold text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`}>← All rides</Link>
      {q.isPending && <p role="status" className="m-0 text-[14px] text-paper-dim">Loading…</p>}
      {q.isError && <p role="alert" className="m-0 text-[14px] text-radar-red">Could not load this ride. {describeError(q.error)}</p>}

      {r && draft && (
        <>
          <header className="flex flex-col gap-2">
            <span className="label">{section.label} · Readers Rides · {STATUS_LABEL[r.status] ?? r.status}</span>
            <h1 className="m-0 font-serif text-[36px] font-medium leading-[1.05] tracking-tight text-paper">{r.title}</h1>
            <p className="m-0 text-[14px] text-paper-dim">
              {r.entrant.name} · {[r.entrant.town, r.entrant.region].filter(Boolean).join(', ')} · <a className="underline" href={`mailto:${r.entrant.email}`}>{r.entrant.email}</a>
              {r.entrant.instagram ? ` · @${r.entrant.instagram.replace(/^@/, '')}` : ''}
              {r.submitted_at ? ` · entered ${etDate(r.submitted_at)}` : ''}{r.channel ? ` · via ${r.channel}` : ''}
            </p>
            <CheckChips checks={r.checks} />
            {r.shortlisted && <span className="w-fit rounded-full bg-amber-glow/20 px-2.5 py-0.5 text-[12px] font-semibold text-amber-deep">Shortlisted</span>}
            {r.awards.filter((a) => a.kind !== 'trial').map((a) => (
              <span key={a.award_id} className="w-fit rounded-full bg-amber-glow/20 px-2.5 py-0.5 text-[12px] font-semibold text-amber-deep">
                ★ {awardLabel(a.kind, a.period, a.kind === 'challenge' ? r.challenge?.theme : null)}{a.grant_result === 'refused' ? ' · grant after cutover' : ''}
              </span>
            ))}
            {r.status === 'declined' && r.decline_reason && <p className="m-0 text-[13px] text-paper-dim">Declined: {r.decline_reason}</p>}
            {r.status === 'retired' && r.retire_reason && <p className="m-0 text-[13px] text-paper-dim">Retired. Readers see: “{r.retire_reason}”</p>}
            {r.status === 'merged' && r.merged_into_id && <p className="m-0 text-[13px] text-paper-dim">Merged into <Link className="underline" to={`${ridesHref(section.path)}/${r.merged_into_id}`}>ride {r.merged_into_id}</Link>.</p>}
          </header>

          {message && <p role="status" className="m-0 rounded-lg bg-radar-cyan/15 px-3 py-2 text-[14px] text-radar-cyan">{message}</p>}
          {error && <p role="alert" className="m-0 rounded-lg bg-radar-red/15 px-3 py-2 text-[14px] text-radar-red">{error}</p>}

          <section aria-label="Photos" className="flex flex-col gap-3">
            <h2 className="m-0 font-serif text-[22px] font-medium">Photos · {draft.photos.length}</h2>
            <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 xl:grid-cols-3">
              {draft.photos.map((p, i) => {
                const ph = photoById.get(p.id)
                const locked = ph?.published === true
                return (
                  <li key={p.id} className="flex flex-col gap-2 rounded-xl border border-ink-500 bg-ink-700 p-3">
                    {ph?.url ? <img src={ph.url} alt={`Photo ${i + 1} of ${draft.photos.length}`} className="aspect-[4/3] w-full rounded-lg object-cover" /> : <div className="aspect-[4/3] w-full rounded-lg bg-ink-600" />}
                    <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-paper-faint">
                      <span>#{i + 1}</span>
                      {ph?.width && ph?.height ? <span>{ph.width}×{ph.height}</span> : null}
                      {ph?.small && <span className="rounded-full bg-amber-glow/20 px-2 py-0.5 font-semibold text-amber-deep">Small</span>}
                      {draft.cover === p.id && <span className="rounded-full bg-radar-cyan/15 px-2 py-0.5 font-semibold text-radar-cyan">Cover</span>}
                      {locked && <span>Published</span>}
                    </div>
                    {editor && (
                      <div className="flex flex-wrap gap-1.5">
                        <button type="button" className={btnSm} aria-label={`Move photo ${i + 1} up`} disabled={i === 0} onClick={() => set({ photos: moveItem(draft.photos, i, -1) })}>Up</button>
                        <button type="button" className={btnSm} aria-label={`Move photo ${i + 1} down`} disabled={i === draft.photos.length - 1} onClick={() => set({ photos: moveItem(draft.photos, i, 1) })}>Down</button>
                        <button type="button" className={btnSm} aria-pressed={draft.cover === p.id} onClick={() => set({ cover: p.id })}>Make cover</button>
                        <button type="button" className={btnSm} disabled={locked} title={locked ? 'Already published' : undefined}
                          onClick={() => set({ photos: draft.photos.map((x) => (x.id === p.id ? { ...x, kind: x.kind === 'finished' ? 'build' : 'finished' } : x)) })}>
                          {p.kind === 'finished' ? 'Finished' : 'Build'} · switch
                        </button>
                      </div>
                    )}
                    {!editor && <span className="text-[12px] text-paper-dim">{p.kind === 'finished' ? 'Finished ride' : 'Build photo'}</span>}
                  </li>
                )
              })}
            </ul>
            <p className="m-0 text-[12px] text-paper-faint">Finished photos go public when you approve. Build photos stay with the entry.</p>
          </section>

          <section aria-label="Details" className="grid gap-4 md:grid-cols-2">
            <label className={lab}>Ride name
              <input className={field} value={draft.title} disabled={!editor} onChange={(e) => set({ title: e.target.value })} />
            </label>
            <label className={lab}>Class
              <select className={field} value={draft.class_slug} disabled={!editor} onChange={(e) => set({ class_slug: e.target.value })}>
                {CLASSES.map(([s, l]) => <option key={s} value={s}>{l}</option>)}
              </select>
            </label>
            <label className={lab}>Started from
              <input className={field} value={draft.started_from_text} disabled={!editor} onChange={(e) => set({ started_from_text: e.target.value })} />
              {r.model_key && <span className="text-[12px] font-normal text-paper-faint">Matched model: {r.model_key}</span>}
            </label>
            <label className={lab}>Home track
              <input className={field} value={draft.home_track} disabled={!editor} onChange={(e) => set({ home_track: e.target.value })} />
            </label>
            <label className={`${lab} md:col-span-2`}>Story
              <textarea className={`${field} min-h-40 py-2`} value={draft.story} disabled={!editor} onChange={(e) => set({ story: e.target.value })} />
            </label>
            <fieldset className="m-0 flex flex-col gap-2 border-0 p-0 md:col-span-2">
              <legend className="mb-1 font-ui text-[13px] font-semibold text-paper-dim">Parts</legend>
              {draft.parts.map((p, i) => (
                <div key={i} className="flex flex-wrap gap-2">
                  <select aria-label={`Part ${i + 1} type`} className={`${field} w-44`} value={p.type} disabled={!editor}
                    onChange={(e) => set({ parts: draft.parts.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)) })}>
                    {PART_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <input aria-label={`Part ${i + 1} text`} className={`${field} min-w-0 flex-1`} value={p.text} disabled={!editor} maxLength={200}
                    onChange={(e) => set({ parts: draft.parts.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) })} />
                  {editor && <button type="button" className={btnSm} onClick={() => set({ parts: draft.parts.filter((_, j) => j !== i) })}>Remove</button>}
                </div>
              ))}
              {editor && <button type="button" className={`${btn} w-fit`} onClick={() => set({ parts: [...draft.parts, { type: PART_TYPES[0], text: '' }] })}>Add a part</button>}
              {draft.parts.length === 0 && !editor && <span className="text-[13px] text-paper-faint">No parts listed.</span>}
            </fieldset>
          </section>

          {editor ? (
            <section aria-label="Actions" className="flex flex-col gap-3 border-t border-ink-500 pt-4">
              {dirty && <p className="m-0 text-[13px] text-amber-deep">You have changes that are not saved. Save them before you approve, ask for photos, decline, shortlist, merge or retire.</p>}
              <div className="flex flex-wrap gap-2">
                <button type="button" className={btn} disabled={!dirty || busy} onClick={() => save.mutate(st.draft as Draft)}>{save.isPending ? 'Saving…' : 'Save changes'}</button>
                {reviewable && <button type="button" className={btnPrimary} disabled={dirty || busy} onClick={() => doApprove.mutate()}>{doApprove.isPending ? 'Approving…' : 'Approve'}</button>}
                {reviewable && <button type="button" className={btn} disabled={dirty || busy} onClick={() => doAsk.mutate()}>Ask for photos</button>}
                {r.status !== 'declined' && r.status !== 'merged' && r.status !== 'retired' && (
                  <button type="button" className={btn} aria-pressed={r.shortlisted} disabled={dirty || busy} onClick={() => doStar.mutate()}>{r.shortlisted ? 'Remove from shortlist' : 'Shortlist'}</button>
                )}
                {reviewable && <button type="button" className={btnDanger} disabled={dirty || busy} onClick={() => setConfirm('decline')}>Decline…</button>}
                {r.status !== 'merged' && <button type="button" className={btnDanger} disabled={dirty || busy} onClick={() => setConfirm('merge')}>Merge into another ride…</button>}
                {newPhotos > 0 && <button type="button" className={btnPrimary} disabled={dirty || busy} onClick={() => doPublishNew.mutate()}>{doPublishNew.isPending ? 'Publishing…' : publishNewLabel(newPhotos)}</button>}
                {r.status === 'live' && <button type="button" className={btnPrimary} disabled={dirty || busy} onClick={() => setConfirm('award')}>Award…</button>}
                <a className={btn} href={magazinePackageHref(pub, id)} download>Magazine package</a>
                {r.status === 'live' && <button type="button" className={btnDanger} disabled={dirty || busy} onClick={() => setConfirm('retire')}>Retire…</button>}
                {r.status === 'retired' && <button type="button" className={btn} disabled={dirty || busy} onClick={() => doRestore.mutate()}>Restore</button>}
              </div>

              {confirm === 'award' && (
                <AwardPanel pub={pub} ride={r} onDone={() => { void refresh() }} onCancel={() => setConfirm(null)} />
              )}
              {confirm === 'decline' && (
                <div role="group" aria-label="Confirm decline" className="flex flex-col gap-3 rounded-xl border border-radar-red/60 bg-ink-700 p-4">
                  <strong>Decline this ride?</strong>
                  <p className="m-0 text-[13px] text-paper-dim">The builder gets an email with the reason you choose.</p>
                  <label className={lab}>Reason
                    <select className={field} value={reason} onChange={(e) => setReason(e.target.value as DeclineReason)}>
                      {DECLINE_REASONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                    </select>
                  </label>
                  <label className={lab}>{reason === 'other' ? 'Say why (required)' : 'Add a note (optional)'}
                    <textarea className={`${field} min-h-20 py-2`} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
                  </label>
                  <div className="flex gap-2">
                    <button type="button" className={btnDanger} disabled={dirty || busy || (reason === 'other' && !note.trim())} onClick={() => doDecline.mutate()}>Yes, decline and tell the builder</button>
                    <button type="button" className={btn} onClick={() => setConfirm(null)}>Cancel</button>
                  </div>
                </div>
              )}
              {confirm === 'merge' && (
                <div role="group" aria-label="Confirm merge" className="flex flex-col gap-3 rounded-xl border border-radar-red/60 bg-ink-700 p-4">
                  <strong>Merge this ride into another?</strong>
                  <p className="m-0 text-[13px] text-paper-dim">Use this when the same build was entered twice. This entry stops being its own ride.</p>
                  <label className={lab}>Number of the ride to keep
                    <input className={`${field} w-40`} inputMode="numeric" value={intoId} onChange={(e) => setIntoId(e.target.value.replace(/\D/g, ''))} />
                  </label>
                  <div className="flex gap-2">
                    <button type="button" className={btnDanger} disabled={dirty || busy || !intoId || Number(intoId) === id} onClick={() => doMerge.mutate()}>Yes, merge</button>
                    <button type="button" className={btn} onClick={() => setConfirm(null)}>Cancel</button>
                  </div>
                </div>
              )}
              {confirm === 'retire' && (
                <div role="group" aria-label="Confirm retire" className="flex flex-col gap-3 rounded-xl border border-radar-red/60 bg-ink-700 p-4">
                  <strong>Retire this ride?</strong>
                  <p className="m-0 text-[13px] text-paper-dim">The ride leaves the hub. Its page stays up with a note instead of the build. You can restore it later.</p>
                  <label className={lab}>Reason. Readers will see this reason on the ride’s page, so write it for them.
                    <textarea className={`${field} min-h-20 py-2`} maxLength={500} value={retireWhy} onChange={(e) => setRetireWhy(e.target.value)} />
                  </label>
                  <div className="flex gap-2">
                    <button type="button" className={btnDanger} disabled={dirty || busy} onClick={() => doRetire.mutate()}>Yes, retire this ride</button>
                    <button type="button" className={btn} onClick={() => setConfirm(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </section>
          ) : (
            <p className="m-0 border-t border-ink-500 pt-4 text-[13px] text-paper-faint">An editor approves, declines and edits rides.</p>
          )}
        </>
      )}
    </div>
  )
}
